import {
    ArrowPathIcon,
    ArrowUpCircleIcon,
    BoltIcon,
    ArrowUturnLeftIcon,
    PlayCircleIcon
} from '@heroicons/react/24/outline';
import type { TaskActionItem } from '../TaskActionsBox';
import type { VirtualAppointmentDto } from '../../../../services/virtualAppointments';
import type { VirtualAppointmentLoadStatus } from '@odo/shared-ui/clinical/virtualAppointments/types';
import { CLINICAL_VA_NEEDED_STATUS_ID, DM_VA_NEEDED_STATUS_ID } from '../../constants';
import { continueTreatmentGate, escalateToClinicalGate, handBackToDmGate } from './vaHandoffGates';
import { vaTriggerGate, type VaTriggerGate } from './vaTriggerGate';
import type { VaHandoffKind } from './useVaHandoff';

export type VaTriggerKind = 'refinement' | 'retention';

// Workspaces the VA step can swap to and come back from.
export type VaWorkspaceFlow = VaTriggerKind | 'manage-monitoring';

const DM_NOT_LINKED_REASON = 'Patient must be created in Dental Monitoring first.';

export type VaActionItem = TaskActionItem;

export interface VaActionInput {
    statusId: string;
    loadStatus: VirtualAppointmentLoadStatus;
    open: readonly VirtualAppointmentDto[];
    isDmLinked: boolean;
    onOpenFlow: (flow: VaWorkspaceFlow) => void;
    onHandoff: (kind: VaHandoffKind) => void;
    /** Opens VAs whose DM notification already fired. */
    onOpenNow: () => void;
}

function gatedItem(
    base: Omit<VaActionItem, 'disabled' | 'disabledReason'>,
    gate: VaTriggerGate
): VaActionItem {
    return { ...base, disabled: !gate.enabled, disabledReason: gate.blockedReason };
}

const TRIGGER_LABELS: Record<VaTriggerKind, string> = {
    refinement: 'Trigger refinement',
    retention: 'Trigger retention'
};

const HANDOFF_ITEMS: Record<VaHandoffKind, Pick<VaActionItem, 'label' | 'icon'>> = {
    'escalate-to-clinical': { label: 'Escalate to Clinical', icon: ArrowUpCircleIcon },
    'hand-back-to-dm': { label: 'Hand back to DM', icon: ArrowUturnLeftIcon },
    'continue-treatment': { label: 'Continue treatment', icon: PlayCircleIcon }
};

function triggerItem(kind: VaTriggerKind, input: VaActionInput): VaActionItem {
    const gate = vaTriggerGate(input.loadStatus, input.open.length);
    const base = { key: kind, label: TRIGGER_LABELS[kind], icon: ArrowPathIcon };
    return gatedItem({ ...base, onSelect: () => input.onOpenFlow(kind) }, gate);
}

// Same gate as Manage Tx: DM actions need the patient in Dental Monitoring; open VAs don't matter.
function manageMonitoringItem(input: VaActionInput): VaActionItem {
    const gate: VaTriggerGate = input.isDmLinked
        ? { enabled: true }
        : { enabled: false, blockedReason: DM_NOT_LINKED_REASON };
    const base = { key: 'manage-monitoring', label: 'Manage monitoring', icon: PlayCircleIcon };
    return gatedItem({ ...base, onSelect: () => input.onOpenFlow('manage-monitoring') }, gate);
}

function handoffItem(kind: VaHandoffKind, input: VaActionInput, gate: VaTriggerGate): VaActionItem {
    return gatedItem(
        { key: kind, ...HANDOFF_ITEMS[kind], onSelect: () => input.onHandoff(kind) },
        gate
    );
}

interface StepSwitch {
    kind: VaHandoffKind;
    gate: (
        status: VirtualAppointmentLoadStatus,
        open: readonly VirtualAppointmentDto[]
    ) => VaTriggerGate;
}

// Each VA step can pass the case to the other team's step; other statuses have no switch.
const STEP_SWITCHES: Readonly<Record<string, StepSwitch>> = {
    [DM_VA_NEEDED_STATUS_ID]: { kind: 'escalate-to-clinical', gate: escalateToClinicalGate },
    [CLINICAL_VA_NEEDED_STATUS_ID]: { kind: 'hand-back-to-dm', gate: handBackToDmGate }
};

function openNowItem(input: VaActionInput): VaActionItem {
    return { key: 'open-now', label: 'Add VAs', icon: BoltIcon, onSelect: input.onOpenNow };
}

// Available whatever the VA step, ahead of the step's own ways out.
function globalItems(input: VaActionInput): VaActionItem[] {
    return [manageMonitoringItem(input), openNowItem(input)];
}

function stepItems(input: VaActionInput): VaActionItem[] {
    const { statusId, loadStatus, open } = input;
    const stepSwitch = STEP_SWITCHES[statusId];
    const switchItems = stepSwitch
        ? [handoffItem(stepSwitch.kind, input, stepSwitch.gate(loadStatus, open))]
        : [];
    return [
        triggerItem('refinement', input),
        triggerItem('retention', input),
        ...switchItems,
        handoffItem('continue-treatment', input, continueTreatmentGate(loadStatus, open.length))
    ];
}

export function buildVaActionItems(input: VaActionInput): VaActionItem[] {
    return [...globalItems(input), ...stepItems(input)];
}
